/**
 * Notifications on this device (mobile M6).
 *
 * Two transports, one switch in Settings → Notifications:
 *
 *  * **The Android app** — Firebase Cloud Messaging through
 *    `@capacitor/push-notifications`. Only in an APK built with the owner's
 *    Firebase file (`google-services.json`): without it the plugin's register
 *    call crashes the app outright, so the build stamps whether it is there
 *    (`__MYDECK_FCM__`, vite.config.ts) and this refuses in words instead.
 *  * **Browsers and the iPhone home-screen app** — Web Push, through the
 *    service worker (`src/sw.js`), which also shows the notification and opens
 *    the page it names when tapped. On an iPhone this needs iOS 16.4+ and the
 *    app added to the Home Screen.
 *
 * The server holds where to send (its subscription list), so turning off here
 * deletes this device's entry there.
 */

import { isAndroidApp, isNative } from "./index";

/** Whether this APK was built with a Firebase file. `false` off-native. */
const FCM_BUILD: boolean = __MYDECK_FCM__;

export type PushSupport = "android" | "webpush" | "none";

export function pushSupport(): PushSupport {
  if (isAndroidApp()) return FCM_BUILD ? "android" : "none";
  if (isNative()) return "none";
  if (typeof window === "undefined") return "none";
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window ? "webpush" : "none";
}

/** Why push can't work here, in words, or null when it can. */
export function pushUnavailableReason(): string | null {
  if (isAndroidApp() && !FCM_BUILD) {
    return "This build of the app was made without its Firebase file, so it can't receive notifications. The next release will include it.";
  }
  if (pushSupport() === "none") {
    return "This browser can't receive notifications. On an iPhone, add MyDeck to the Home Screen first (iOS 16.4 or later).";
  }
  return null;
}

const ON_KEY = "mydeck.push.target";

/** The target this device last registered, if any (to unregister it later). */
export function registeredTarget(): string | null {
  try {
    return localStorage.getItem(ON_KEY);
  } catch {
    return null;
  }
}

function remember(target: string | null): void {
  try {
    if (target) localStorage.setItem(ON_KEY, target);
    else localStorage.removeItem(ON_KEY);
  } catch {
    /* the server still has the subscription; only "on" state is lost */
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type Register = (kind: "fcm" | "webpush", target: string) => Promise<unknown>;

/**
 * Ask permission and register this device. Resolves to null on success, or a
 * sentence saying why not (denied, not supported, not set up on the server).
 */
export async function enablePush(webpushKey: string | null, register: Register): Promise<string | null> {
  const support = pushSupport();
  if (support === "none") return pushUnavailableReason();

  if (support === "android") {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return "Notifications are blocked for MyDeck in Android's settings.";
    // The channel the server's messages name (app/push/transports.py).
    await PushNotifications.createChannel({
      id: "activity",
      name: "Card activity",
      description: "Transactions, statements, bills due",
      importance: 4,
    }).catch(() => undefined);
    const token = await new Promise<string>((resolve, reject) => {
      void PushNotifications.addListener("registration", (t) => resolve(t.value));
      void PushNotifications.addListener("registrationError", (e) => reject(new Error(e.error)));
      void PushNotifications.register();
    }).catch(() => null);
    if (!token) return "Android didn't hand out a notification token. Try again in a moment.";
    await register("fcm", token);
    remember(token);
    return null;
  }

  if (!webpushKey) return "Notifications aren't set up on the server yet.";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "Notifications are blocked for this site in the browser's settings.";
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(webpushKey) }));
  const target = JSON.stringify(sub);
  await register("webpush", target);
  remember(target);
  return null;
}

/** Stop notifications on this device: forget it here and on the server. */
export async function disablePush(unregister: (target: string) => Promise<unknown>): Promise<void> {
  const target = registeredTarget();
  if (pushSupport() === "webpush") {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    await sub?.unsubscribe().catch(() => undefined);
  }
  if (target) await unregister(target).catch(() => undefined);
  remember(null);
}

/**
 * Open the page a tapped notification names. Android only: in a browser the
 * service worker does it (src/sw.js). Paths only, never another site.
 */
export function onNotificationOpened(open: (path: string) => void): void {
  if (!isAndroidApp() || !FCM_BUILD) return;
  void import("@capacitor/push-notifications").then(({ PushNotifications }) =>
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const url = (action.notification.data as { url?: unknown } | undefined)?.url;
      if (typeof url === "string" && /^\/(?![/\\])/.test(url)) open(url);
    }),
  );
}
